import { flow, types } from "mobx-state-tree";
import { api } from "../service/api";
import { serviceCoach } from "../utils/env";

export interface ILightdashEmbed {
  url: string;
  expiresAt: string;
}

// Used only if the embed token doesn't carry an "exp" claim.
const DEFAULT_EMBED_TTL_MS = 60 * 60 * 1000;

// The embed token is a JWT in the URL hash; only its "exp" claim is read.
const getEmbedExpiresAt = (url: string): string => {
  try {
    const payload = url.split("#")[1]?.split(".")[1];
    const { exp } = JSON.parse(atob(payload.replace(/-/g, "+").replace(/_/g, "/")));
    if (typeof exp === "number") return new Date(exp * 1000).toISOString();
  } catch {
    // Malformed token: fall back to the default TTL.
  }
  return new Date(Date.now() + DEFAULT_EMBED_TTL_MS).toISOString();
};

// The embed URL carries an access token: it's returned to the caller and never kept in the store, logged or persisted.
const LightdashStore = types
  .model({
    isLoading: types.optional(types.boolean, false),
  })
  .views((self) => ({}))
  .actions((self) => ({
    fetchDashboardEmbed: flow(function* () {
      try {
        self.isLoading = true;
        const { data } = yield api.get(`${serviceCoach}/api/v1/lightdash/dashboard-embed`);
        const url = data?.url;

        if (typeof url !== "string" || !url) {
          throw new Error("Invalid Lightdash embed response");
        }

        const embed: ILightdashEmbed = { url, expiresAt: getEmbedExpiresAt(url) };
        return embed;
      } catch (error: any) {
        console.log("Error fetchDashboardEmbed:", error?.message);
        throw error;
      } finally {
        self.isLoading = false;
      }
    }),
  }));

export const lightdashStore = LightdashStore.create();
